/**
 * Variant dropdown rendered on product cards by `snippets/quick-add.liquid`.
 *
 * Each <option> carries its variant's preformatted price and availability, so a change
 * is applied locally - no section re-render: it updates the form's variant id, the add
 * button state, the card price and the card links.
 */
class CardVariantSelect extends HTMLElement {
  connectedCallback() {
    this.select?.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.#handleChange);
  }

  /** @returns {HTMLSelectElement | null} */
  get select() {
    return this.querySelector('select');
  }

  #handleChange = () => {
    const option = this.select?.selectedOptions[0];
    if (!option) return;

    const available = option.dataset.available === 'true';
    const formComponent = this.closest('product-form-component');

    const variantInput = formComponent?.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = option.value;
      variantInput.disabled = !available;
    }

    const addButton = formComponent?.querySelector('button[type="submit"]');
    if (addButton instanceof HTMLButtonElement) addButton.disabled = !available;

    const productCard = this.closest('product-card');
    if (productCard) {
      this.#updatePrice(productCard, option);
      /** @type {any} */ (productCard).applyVariantToLinks?.(option.value);
    }
  };

  /**
   * @param {Element} productCard
   * @param {HTMLOptionElement} option
   */
  #updatePrice(productCard, option) {
    const { price = '', comparePrice } = option.dataset;
    const { priceRegularLabel = '', priceSaleLabel = '' } = this.dataset;

    const priceContainers = productCard.querySelectorAll("product-price [ref='priceContainer']");
    for (const container of priceContainers) {
      if (container.closest('dialog')) continue;

      container.replaceChildren();
      if (comparePrice) {
        container.append(
          this.#priceGroup('price-item__group price-item--regular compare-at-price', priceRegularLabel, comparePrice),
          this.#priceGroup('price-item__group price', priceSaleLabel, price)
        );
      } else {
        const span = document.createElement('span');
        span.className = 'price';
        span.textContent = price;
        container.append(span);
      }
    }

    // The volume pricing note describes the product-wide range, not a single variant.
    productCard.querySelector("product-price [ref='volumePricingNote']")?.setAttribute('hidden', '');
  }

  /**
   * @param {string} className
   * @param {string} label
   * @param {string} value
   */
  #priceGroup(className, label, value) {
    const group = document.createElement('span');
    group.className = className;

    const hiddenLabel = document.createElement('span');
    hiddenLabel.className = 'visually-hidden';
    hiddenLabel.textContent = `${label} `;

    group.append(hiddenLabel, value);
    return group;
  }
}

if (!customElements.get('card-variant-select')) {
  customElements.define('card-variant-select', CardVariantSelect);
}
